import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, MapPin, Sparkles } from "lucide-react";
import { INDUSTRIES, INDUSTRY_LIST, type Industry, type IndustrySlug } from "@/data/industries";
import { INDUSTRY_SEO } from "@/data/industry-seo";
import { STATES_DATA } from "@/data/states";
import { ogImageMeta } from "@/lib/og-image";
import hospitalImg from "@/assets/hospital-vending-machines-3-bay.png.asset.json";
import { leafCanonical } from "@/lib/canonical";

const SLUGS = new Set<string>(INDUSTRY_LIST.map((i) => i.slug));

const INDUSTRY_IMAGES: Partial<Record<IndustrySlug, { url: string; alt: string }>> = {
  "hospitals-clinics": {
    url: hospitalImg.url,
    alt: "Free vending machines in a USA hospital emergency ward — snack, drink and combo bays for 24/7 staff and visitor access",
  },
};


export const Route = createFileRoute("/industries/$industry")({
  beforeLoad: ({ params }) => {
    if (!SLUGS.has(params.industry)) throw notFound();
  },
  loader: ({ params }) => ({ industry: INDUSTRIES[params.industry as IndustrySlug] }),
  head: ({ params, loaderData, match, matches }) => {
    const ind = loaderData?.industry ?? INDUSTRIES[params.industry as IndustrySlug];
    const seo = INDUSTRY_SEO[params.industry as IndustrySlug];
    const url = `https://vendingmachinefree.com/industries/${params.industry}`;
    const title = seo?.seoTitle ?? `Free ${ind.name} Vending Placement | USA`;
    const description =
      seo?.seoDescription ??
      `Get FREE vending machines for ${ind.name.toLowerCase()} anywhere in the USA. Full-service installation, restocking & maintenance at no cost. Apply now for fast approval.`;
    const industryImg = INDUSTRY_IMAGES[params.industry as IndustrySlug];
    const ogImg = industryImg
      ? `https://vendingmachinefree.com${industryImg.url}`
      : undefined;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...(seo?.keywords?.length ? [{ name: "keywords", content: seo.keywords.join(", ") }] : []),
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        ...(ogImg
          ? [
              { property: "og:image", content: ogImg },
              { property: "og:image:alt", content: industryImg!.alt },
              { name: "twitter:card", content: "summary_large_image" },
              { name: "twitter:image", content: ogImg },
            ]
          : ogImageMeta()),
      ],
      links: leafCanonical(url, { match, matches }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "LocalBusiness",
                name: "Vending Machine Free.com",
                url: "https://vendingmachinefree.com",
                image: ogImg ?? "https://vendingmachinefree.com/og-image.jpg",
                areaServed: { "@type": "Country", name: "United States" },
                description: `Free vending machine placement, installation, restocking, and maintenance for ${ind.name.toLowerCase()} across the USA.`,
              },
              {
                "@type": "Service",
                serviceType: `Free Vending Machine Placement for ${ind.name}`,
                provider: { "@type": "Organization", name: "Vending Machine Free.com", url: "https://vendingmachinefree.com" },
                areaServed: { "@type": "Country", name: "United States" },
                offers: {
                  "@type": "Offer",
                  price: "0",
                  priceCurrency: "USD",
                  description: "Free vending machine placement with full-service install, restocking, and maintenance.",
                },
                hasOfferCatalog: {
                  "@type": "OfferCatalog",
                  name: "Vending Machine Types",
                  itemListElement: ind.machines.map((m) => ({
                    "@type": "Offer",
                    itemOffered: { "@type": "Service", name: m.title, description: m.text },
                  })),
                },
              },
              {
                "@type": "FAQPage",
                mainEntity: ind.faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "Home", item: "https://vendingmachinefree.com/" },
                  { "@type": "ListItem", position: 2, name: "Industries", item: "https://vendingmachinefree.com/industries" },
                  { "@type": "ListItem", position: 3, name: ind.name, item: url },
                ],
              },
            ],
          }),
        },
      ],
    };
  },
  component: IndustryPage,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="font-display text-3xl">Industry not found</h1>
        <Link to="/" className="mt-4 inline-block text-safety underline">Return home</Link>
      </div>
    </div>
  ),
  errorComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <p>Something went wrong. <Link to="/" className="text-safety underline">Go home</Link>.</p>
    </div>
  ),
});

function IndustryPage() {
  const { industry: ind } = Route.useLoaderData() as { industry: Industry };
  const seo = INDUSTRY_SEO[ind.slug];
  const stateNameBySlug = new Map(STATES_DATA.map((s) => [s.slug, s.name] as const));
  const heroImg = INDUSTRY_IMAGES[ind.slug];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background sticky top-0 z-40">
        <div className="container-x flex h-16 items-center justify-between">
          <Link to="/" className="font-display text-xl uppercase">
            Vending Machine Free<span className="text-safety">.com</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/coverage" className="hover:text-safety">Coverage</Link>
            <Link to="/vending-machines" className="hover:text-safety">Machines</Link>
            <Link to="/contact" className="hover:text-safety">Contact</Link>
          </nav>
          <Link
            to="/"
            hash="contact"
            className="inline-flex items-center gap-2 rounded-md bg-safety px-4 py-2 text-sm font-semibold text-safety-foreground"
          >
            Apply Free <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 bg-charcoal text-charcoal-foreground">
        <div className="container-x grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] lg:items-center">
          <div className="min-w-0 max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
              Industry · Nationwide USA
            </div>
            <h1 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl uppercase">
              Free Vending Machines for {ind.name} — USA Nationwide Placement
            </h1>
            <p className="mt-6 text-lg text-white/85 max-w-3xl">{ind.heroTagline}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                hash="contact"
                className="inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
              >
                Get FREE Vending Machines — USA Nationwide Services
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-md border border-white/30 px-6 py-3 font-semibold text-white hover:bg-white/10"
              >
                Apply Now for Fast Approval
              </Link>
            </div>
          </div>
          {heroImg && (
            <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl bg-black/20">
              <img
                src={heroImg.url}
                alt={heroImg.alt}
                width={1600}
                height={800}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-auto object-cover"
              />
            </div>
          )}
        </div>
      </section>



      {/* Intro + Deep Dive */}
      <section className="py-16 md:py-20">
        <div className="container-x max-w-4xl">
          <p className="text-lg text-foreground leading-relaxed">{ind.intro}</p>
          <div className="mt-6 space-y-5 text-base text-muted-foreground leading-relaxed">
            {ind.deepDive.map((p, i) => (<p key={i}>{p}</p>))}
          </div>
          {seo?.contentAngle && (
            <div className="mt-8 rounded-lg border-l-4 border-safety bg-secondary p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
                <Sparkles className="h-4 w-4" /> Why It Works
              </div>
              <p className="mt-2 text-base text-foreground leading-relaxed">{seo.contentAngle}</p>
            </div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            Why {ind.name} Benefit From Free Vending Machines
          </h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ind.benefits.map((b) => (
              <div key={b.title} className="rounded-lg bg-card border border-border p-6">
                <div className="flex items-center gap-2 text-safety font-bold">
                  <CheckCircle2 className="h-5 w-5" /> {b.title}
                </div>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Machine Types */}
      <section className="py-16 md:py-20">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">Machine Types Available</h2>
          <p className="mt-3 text-center text-muted-foreground max-w-2xl mx-auto">
            The right mix for {ind.name.toLowerCase()} — installed and serviced at zero cost.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {ind.machines.map((m) => (
              <div key={m.title} className="rounded-lg border border-border p-6 bg-card">
                <h3 className="font-display text-lg uppercase text-steel">{m.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Options */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">Product Options</h2>
          <p className="mt-3 text-center text-muted-foreground">
            Curated product mixes tailored to {ind.name.toLowerCase()}.
          </p>
          <ul className="mt-8 grid sm:grid-cols-2 gap-3">
            {ind.products.map((p) => (
              <li key={p} className="flex items-start gap-3 rounded-md bg-card border border-border p-4">
                <CheckCircle2 className="h-5 w-5 text-safety shrink-0 mt-0.5" />
                <span className="text-sm font-medium">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 md:py-20">
        <div className="container-x max-w-4xl">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">How Free Placement Works</h2>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            {[
              { n: "1", t: "Apply Online", d: `Complete the ${ind.name} application in under 2 minutes.` },
              { n: "2", t: "Approval & Site Survey", d: "Fast approval and a free on-site assessment." },
              { n: "3", t: "Delivery & Install", d: "Machines delivered, installed, and stocked at zero cost." },
              { n: "4", t: "Restock & Maintain", d: "Ongoing restocking, cleaning, and 24/7 repair — all free." },
            ].map((s) => (
              <div key={s.n} className="rounded-lg border border-border p-5 bg-card">
                <div className="font-display text-safety text-3xl">{s.n}</div>
                <h3 className="mt-1 font-display uppercase">{s.t}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commission */}
      <section className="py-14 bg-steel text-white">
        <div className="container-x max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
            Commission Eligibility
          </div>
          <h2 className="mt-3 font-display text-2xl md:text-3xl uppercase">
            Larger {ind.name} Locations May Qualify for Ongoing Commission
          </h2>
          <p className="mt-4 text-white/85 leading-relaxed">
            Large workplaces with 100–1,000+ staff or high foot-traffic locations may qualify for ongoing commission
            payments based on machine sales. This optional revenue share program is available for qualifying USA
            businesses.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-safety px-6 py-3 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
          >
            Check Commission Eligibility <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* USA Nationwide Coverage */}
      <section className="py-16 md:py-20">
        <div className="container-x max-w-4xl text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase">USA Nationwide Coverage</h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            We provide FREE vending machine placement for {ind.name.toLowerCase()} across the entire United States,
            including all 50 states, all major metros, and thousands of secondary markets. Explore our full{" "}
            <Link to="/coverage" className="text-safety underline font-semibold">nationwide coverage map</Link> or browse
            by state and city below.
          </p>
        </div>
      </section>

      {/* Popular Cities */}
      <section className="py-16 md:py-20 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            Popular Cities for {ind.name}
          </h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {ind.cities.map((c) => (
              <Link
                key={`${c.state}-${c.city}`}
                to="/states/$state/$town"
                params={{ state: c.state, town: c.city }}
                className="flex items-center gap-2 rounded-md bg-card border border-border px-4 py-3 text-sm hover:border-safety hover:text-safety transition"
              >
                <MapPin className="h-4 w-4 text-safety shrink-0" />
                <span className="font-medium">{c.label}</span>
              </Link>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/coverage"
              className="inline-flex items-center gap-2 rounded-md border border-charcoal px-6 py-3 font-semibold hover:bg-charcoal hover:text-white transition"
            >
              Browse All 50 States <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Local SEO Multiplier — [Placement Phrase] + City/State */}
      {seo?.placementPhrase && (
        <section className="py-16 md:py-20 bg-charcoal text-charcoal-foreground">
          <div className="container-x">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-safety font-bold">
                Local SEO Multiplier
              </div>
              <h2 className="mt-3 font-display text-2xl md:text-3xl uppercase">
                {seo.placementPhrase.replace(/\b\w/g, (c) => c.toUpperCase())} — Top Cities & States
              </h2>
              <p className="mt-3 text-white/80">
                National reach, local delivery. Explore <span className="text-safety font-semibold">{seo.placementPhrase}</span>{" "}
                in major US cities and every state we serve.
              </p>
            </div>

            {/* City variants */}
            <div className="mt-10">
              <h3 className="font-display uppercase text-sm text-safety tracking-widest text-center">
                {seo.placementPhrase} — by city
              </h3>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-sm">
                {ind.cities.map((c) => (
                  <Link
                    key={`seo-city-${c.state}-${c.city}`}
                    to="/states/$state/$town"
                    params={{ state: c.state, town: c.city }}
                    className="rounded-md bg-white/5 border border-white/10 px-3 py-2 hover:border-safety hover:text-safety transition"
                  >
                    {seo.placementPhrase} {c.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* State variants */}
            <div className="mt-10">
              <h3 className="font-display uppercase text-sm text-safety tracking-widest text-center">
                {seo.placementPhrase} — by state
              </h3>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 text-sm">
                {STATES_DATA.map((s) => (
                  <Link
                    key={`seo-state-${s.slug}`}
                    to="/states/$state"
                    params={{ state: s.slug }}
                    className="rounded-md bg-white/5 border border-white/10 px-3 py-2 hover:border-safety hover:text-safety transition"
                  >
                    {seo.placementPhrase} {stateNameBySlug.get(s.slug) ?? s.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All 50 States — SEO / Internal Linking */}
      <section className="py-16 md:py-20">
        <div className="container-x">
          <h2 className="font-display text-2xl md:text-3xl uppercase text-center">
            Free {ind.short} Vending Machines — Serving All 50 States
          </h2>
          <p className="mt-3 text-center text-muted-foreground max-w-3xl mx-auto">
            Free vending machine placement for {ind.name.toLowerCase()} in every US state — installed, restocked, and maintained at zero cost.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 text-sm">
            {STATES_DATA.map((s) => (
              <Link
                key={s.slug}
                to="/states/$state"
                params={{ state: s.slug }}
                className="rounded-md border border-border bg-card px-3 py-2 hover:border-safety hover:text-safety transition"
              >
                {ind.short} in {s.name}
              </Link>
            ))}
          </div>
        </div>
      </section>


      {/* FAQs */}
      <section className="py-16 md:py-20">
        <div className="container-x max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl uppercase text-center">
            {ind.name} Vending — FAQs
          </h2>
          <div className="mt-10 space-y-4">
            {ind.faqs.map((f) => (
              <details key={f.q} className="rounded-lg border border-border bg-card p-5 group">
                <summary className="font-semibold cursor-pointer flex justify-between gap-3">
                  {f.q}
                  <span className="text-safety group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related industries */}
      <section className="py-14 bg-secondary">
        <div className="container-x">
          <h2 className="font-display text-2xl uppercase text-center">Explore Other Industries</h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {ind.related.map((slug) => {
              const r = INDUSTRIES[slug];
              return (
                <Link
                  key={slug}
                  to="/industries/$industry"
                  params={{ industry: slug }}
                  className="rounded-lg bg-card border border-border p-5 hover:border-safety transition"
                >
                  <div className="font-display uppercase">{r.name}</div>
                  <div className="mt-1 text-sm text-muted-foreground">Free vending machines for {r.short.toLowerCase()} nationwide.</div>
                  <div className="mt-3 text-safety font-semibold text-sm inline-flex items-center gap-1">
                    View Page <ArrowRight className="h-3.5 w-3.5" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-charcoal text-charcoal-foreground">
        <div className="container-x max-w-3xl text-center">
          <h2 className="font-display text-3xl md:text-4xl uppercase">
            Ready for FREE Vending Machines at Your {ind.short}?
          </h2>
          <p className="mt-4 text-white/85">
            Apply now — most locations approved in 48 hours, installed in 1–2 weeks. Zero cost. Zero commitment.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              hash="contact"
              className="inline-flex items-center gap-2 rounded-md bg-safety px-7 py-3.5 font-semibold text-safety-foreground shadow-[var(--shadow-cta)]"
            >
              Get FREE Vending Machines — USA Nationwide Services <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 px-7 py-3.5 font-semibold text-white hover:bg-white/10"
            >
              Apply Now for Fast Approval
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
